import { useEffect, useRef, type ReactNode } from "react";
import { LockKeyhole, MessageCircle, MicOff, X } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage, cn, GlassMaterial } from "@link/ui";
import "./call-surface.css";

/** Presentation only: media ownership, encryption and call state stay in MediaSession. */
export function CallSurface({ children, header, expanded = false, hidden = false, hasVideo = false }: {
  children: ReactNode; header?: ReactNode; expanded?: boolean; hidden?: boolean; hasVideo?: boolean;
}) {
  const surface = useRef<HTMLElement>(null);
  useEffect(() => {
    if (expanded && !hidden) surface.current?.focus({ preventScroll: true });
  }, [expanded, hidden]);
  return (
    <section ref={surface} tabIndex={-1} className={cn("call-stage", expanded && "call-stage-expanded")} hidden={hidden} data-has-video={hasVideo} aria-label="Call participants and controls">
      <div className="call-stage-art" aria-hidden />
      {header}
      <div className="call-stage-content">{children}</div>
      {expanded && <div className="call-stage-brand" aria-hidden="true"><img src="/images/link-ghost.png" alt="" width={40} height={40} /><span>Link</span></div>}
    </section>
  );
}

export function CallHeader({ name, avatarUrl, subtitle, connected, onMinimize, group = false }: {
  name: string; avatarUrl: string | null; subtitle: string; connected: boolean; onMinimize: () => void; group?: boolean;
}) {
  return (
    <header className="call-glass-header liquid-lens-host">
      <GlassMaterial radius={28} />
      <div className="call-header-avatar" data-connected={connected}>
        <Avatar className="call-identity-avatar h-full w-full">
          {avatarUrl !== null ? <AvatarImage src={avatarUrl} alt="" /> : null}
          <AvatarFallback variant={group ? "initials" : "ghost"}>{name.slice(0, 2).toUpperCase()}</AvatarFallback>
        </Avatar>
        <span className="call-header-presence" aria-hidden="true" />
      </div>
      <div className="call-header-copy"><h2>{name}</h2><p><LockKeyhole size={15} aria-hidden="true" /><span>{subtitle}</span></p></div>
      <button className="call-header-chat" type="button" onClick={onMinimize} title="Back to chat — keep the call running" aria-label="Back to chat, keep call running"><MessageCircle aria-hidden="true" /></button>
      <button className="call-header-close" type="button" onClick={onMinimize} title="Minimize call — does not hang up" aria-label="Minimize call"><X aria-hidden="true" /></button>
    </header>
  );
}

export function CallParticipantBubble({ name, subtitle, avatarUrl, speaking, muted, pulsing = false }: {
  name: string; subtitle?: string; avatarUrl: string | null;
  speaking: boolean; muted: boolean; pulsing?: boolean;
}) {
  return (
    <div className="call-person">
      <div className={cn("call-avatar", pulsing && "call-avatar-ringing")} data-speaking={speaking ? "true" : "false"} data-muted={muted ? "true" : "false"}>
        <Avatar className="call-identity-avatar h-full w-full">
          {avatarUrl !== null ? <AvatarImage src={avatarUrl} alt="" /> : null}
          <AvatarFallback>{name.slice(0, 2).toUpperCase()}</AvatarFallback>
        </Avatar>
        {muted && <span className="call-mute-badge" title={`${name} is muted`} aria-label={`${name} is muted`}><MicOff aria-hidden /></span>}
      </div>
      <p className="call-person-name" title={name}>{name}</p>
      {subtitle !== undefined && <p className="call-person-subtitle" title={subtitle}>{subtitle}</p>}
    </div>
  );
}
